import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Tag, User, Briefcase, DollarSign, Phone, Mail, CreditCard, Activity, Layers, Save } from "lucide-react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { SECTORS, NEEDS, toDatetimeLocal, fromDatetimeLocal } from "@/lib/constants";
import { PAYMENT_PREFS, ONETIME_JOB_STATUSES, FIXED_EXPENSE_ITEMS, ONETIME_FIXED_EXPENSE_ITEMS } from "@/lib/finance";
import { toast } from "sonner";

const label = "text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2";
const input = "w-full h-12 rounded-xl border border-slate-200 bg-slate-50/40 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 placeholder:text-slate-400";

export default function OneTimeJobNew() {
  const { workspace } = useWorkspace();
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [extraSectors, setExtraSectors] = useState([]);
  const [f, setF] = useState({
    date: toDatetimeLocal(new Date().toISOString()),
    brand_name: "", contact_name: "", sector: "", sector_other: "",
    needs: [], amount: "", partial_payment: "", phone: "", email: "",
    payment_pref: "", status: "on_odeme_bekleniyor",
    fixed_expenses: { aylik_cekim: null, aylik_reklamci: null, aylik_yazilim: null, aylik_diger: null },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { (async () => {
    const [{ data: c }, { data: sx }] = await Promise.all([
      api.get("/finance/customers", { params: { workspace, active_only: true } }),
      api.get("/finance/lookups/sectors", { params: { workspace } }),
    ]);
    setCustomers(c); setExtraSectors(sx);
  })(); }, [workspace]);

  const pickCustomer = (brand) => {
    const c = customers.find((x) => x.brand_name === brand); if (!c) return;
    setF((p) => ({ ...p, brand_name: c.brand_name, contact_name: c.contact_name || p.contact_name,
      phone: c.phone || p.phone, email: c.email || p.email, sector: c.sector || p.sector, sector_other: c.sector_other || p.sector_other }));
  };

  const toggleNeed = (v) => setF((p) => ({ ...p, needs: p.needs.includes(v) ? p.needs.filter((x) => x !== v) : [...p.needs, v] }));
  const toggleFixed = (key) => setF((p) => ({ ...p, fixed_expenses: { ...p.fixed_expenses, [key]: p.fixed_expenses[key] == null ? "" : null } }));
  const setFixed = (key, v) => setF((p) => ({ ...p, fixed_expenses: { ...p.fixed_expenses, [key]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.brand_name.trim() || !f.amount) return setError("Marka Adı ve Anlaşma Bedeli zorunlu.");
    setSaving(true);
    try {
      if (f.sector === "diger" && f.sector_other.trim()) {
        await api.post("/finance/lookups/sectors", null, { params: { workspace, label: f.sector_other.trim() } });
      }
      const fixedItems = ONETIME_FIXED_EXPENSE_ITEMS
        .filter((it) => f.fixed_expenses[it.key] != null)
        .map((it) => ({ key: it.key, label: it.label, amount: parseFloat(f.fixed_expenses[it.key] || 0) }));
      await api.post("/finance/onetime-jobs", {
        workspace,
        date: fromDatetimeLocal(f.date) || new Date().toISOString(),
        brand_name: f.brand_name, contact_name: f.contact_name,
        sector: f.sector, sector_other: f.sector_other,
        needs: f.needs, amount: parseFloat(f.amount),
        partial_payment: parseFloat(f.partial_payment || 0),
        phone: f.phone, email: f.email, payment_pref: f.payment_pref || null,
        status: f.status, fixed_expenses: fixedItems,
      });
      toast.success("Tek seferlik iş kaydedildi");
      nav("/finance");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <FinanceLayout>
      <div className="mb-6 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50" data-testid="back-btn">
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Yeni İş Ekleme</h1>
          <p className="text-sm text-slate-500 mt-1">Tek seferlik hizmetler için yeni bir iş oluşturun.</p>
        </div>
      </div>

      <form onSubmit={submit} className="rounded-2xl border border-slate-100 bg-white p-8 pb-24" data-testid="ot-job-form">
        {customers.length > 0 && (
          <div className="mb-6 rounded-xl bg-emerald-50/40 border border-emerald-100 p-4 flex items-center justify-between gap-3">
            <div><div className="text-sm font-semibold text-emerald-800">Mevcut Müşteriden Seç</div>
              <div className="text-xs text-slate-500 mt-0.5">Formu daha önce eklediğiniz müşteri bilgileriyle doldur.</div></div>
            <select onChange={(e) => pickCustomer(e.target.value)} data-testid="oj-picker" className="h-11 min-w-[240px] rounded-lg border border-slate-200 bg-white px-4 text-sm">
              <option value="">Müşteri seçin…</option>
              {customers.map((c) => <option key={c.id} value={c.brand_name}>{c.brand_name}</option>)}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
          <div><div className={label}><Calendar size={14} className="text-emerald-700" />Tarih</div>
            <input type="datetime-local" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={input} data-testid="oj-date" /></div>
          <div><div className={label}><Tag size={14} className="text-emerald-700" />Marka Adı</div>
            <input type="text" value={f.brand_name} onChange={(e) => setF({ ...f, brand_name: e.target.value })} placeholder="Marka adını giriniz" className={input} data-testid="oj-brand" /></div>
          <div><div className={label}><User size={14} className="text-emerald-700" />Yetkili Adı</div>
            <input type="text" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} placeholder="Yetkili adını giriniz" className={input} data-testid="oj-contact" /></div>
          <div><div className={label}><Briefcase size={14} className="text-emerald-700" />Sektör</div>
            <select value={f.sector} onChange={(e) => setF({ ...f, sector: e.target.value, sector_other: e.target.value === "diger" ? f.sector_other : "" })} className={input} data-testid="oj-sector">
              <option value="">Sektör seçiniz</option>
              {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              {extraSectors.map((s) => <option key={s} value={`custom:${s}`}>{s}</option>)}
            </select>
            {f.sector === "diger" && (
              <input value={f.sector_other} onChange={(e) => setF({ ...f, sector_other: e.target.value })} placeholder="Yeni sektör adı" className={input + " mt-2"} data-testid="oj-sector-other" />
            )}
          </div>
          <div className="col-span-2">
            <div className={label}><Layers size={14} className="text-emerald-700" />Çalışma Kapsamı</div>
            <div className="flex flex-wrap gap-2">
              {NEEDS.map((n) => {
                const on = f.needs.includes(n.value);
                return (
                  <button key={n.value} type="button" onClick={() => toggleNeed(n.value)} data-testid={`oj-need-${n.value}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${on ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center ${on ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300"}`}>{on && <svg width="9" height="9" viewBox="0 0 12 12"><path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" fill="none" /></svg>}</span>
                    {n.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div><div className={label}><DollarSign size={14} className="text-emerald-700" />Anlaşma Bedeli</div>
            <input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Tutar giriniz (TL)" className={input} data-testid="oj-amount" /></div>
          <div><div className={label}><DollarSign size={14} className="text-emerald-700" />Kısmi Ödeme (opsiyonel)</div>
            <input type="number" step="0.01" value={f.partial_payment} onChange={(e) => setF({ ...f, partial_payment: e.target.value })} placeholder="Şu ana kadar alınan" className={input} data-testid="oj-partial" /></div>
          <div><div className={label}><Phone size={14} className="text-emerald-700" />Telefon Numarası</div>
            <input type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Telefon numaranızı giriniz" className={input} data-testid="oj-phone" /></div>
          <div><div className={label}><Mail size={14} className="text-emerald-700" />Mail Adresi</div>
            <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Mail adresinizi giriniz" className={input} data-testid="oj-email" /></div>
          <div><div className={label}><CreditCard size={14} className="text-emerald-700" />Ödeme Tercihi</div>
            <select value={f.payment_pref} onChange={(e) => setF({ ...f, payment_pref: e.target.value })} className={input} data-testid="oj-pref">
              <option value="">Ödeme tercihini seçiniz</option>
              {PAYMENT_PREFS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select></div>
          <div className="col-span-2"><div className={label}><Activity size={14} className="text-emerald-700" />Durum</div>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={input} data-testid="oj-status">
              {ONETIME_JOB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select></div>
        </div>

        <div className="mt-8 rounded-xl bg-slate-50/60 border border-slate-100 p-6" data-testid="oj-fixed-block">
          <div className="mb-1"><div className="text-sm font-semibold text-slate-900">Bu İşe Ait Tek Seferlik Gider</div>
            <div className="text-xs text-slate-500">Bu tek seferlik işe bağlı olarak yapılan tek seferlik giderler. Aylık tekrar etmez.</div></div>
          <div className="grid grid-cols-4 gap-4 mt-4">
            {ONETIME_FIXED_EXPENSE_ITEMS.map((it) => {
              const val = f.fixed_expenses[it.key];
              const on = val != null;
              return (
                <div key={it.key} className={`rounded-lg border ${on ? "border-emerald-300 bg-white" : "border-slate-200 bg-white"} p-3`}>
                  <label className="flex items-center gap-2 text-sm text-slate-800 mb-2 cursor-pointer">
                    <input type="checkbox" checked={on} onChange={() => toggleFixed(it.key)} data-testid={`oj-fx-${it.key}`} className="w-4 h-4 accent-emerald-600" />
                    <span className="font-medium">{it.label}</span>
                  </label>
                  <div className="relative">
                    <input type="number" step="0.01" disabled={!on} value={val ?? ""} onChange={(e) => setFixed(it.key, e.target.value)} placeholder="0" data-testid={`oj-fx-amt-${it.key}`}
                      className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 pr-8 text-sm disabled:bg-slate-50 disabled:text-slate-400" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₺</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {error && <div className="mt-6 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3" data-testid="oj-error">{error}</div>}

        <div className="fixed bottom-0 left-[280px] right-0 bg-white border-t border-slate-100 px-8 py-4">
          <div className="max-w-[1320px] flex justify-end">
            <button type="submit" disabled={saving} data-testid="oj-save" className="w-full rounded-xl bg-emerald-800 hover:bg-emerald-700 text-white h-12 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <Save size={15} /> {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>
      </form>
    </FinanceLayout>
  );
}
